<script lang="ts" setup>
import type { MenuOption } from 'naive-ui'
import { requestDeleteFeed } from '~/apis/feed/requestDeleteFeed'
import { fetchNoIndex } from '~/apis/content/fetchNoIndex'

definePageMeta({
  middleware: 'feed'
})

const id = Number(useRoute().params.id?.toString())
const { data: feed } = useNuxtData<Feed.Info>(`feed:${id}`)

if (!feed.value) {
  throw createError({ statusCode: 404, message: '动态不见了' })
}

const imageCount = feed.value.content.match('<img')?.length || 0
const readTime = getReadTime(htmlToText(feed.value.content), imageCount)

const { data: keywords } = await useAsyncData(() =>
  requestTagGenerate({
    title: feed.value?.title,
    content: htmlToText(feed.value?.content),
    seo: true
  })
)

if (feed) {
  const { data: noIndex } = await useAsyncData(() =>
    fetchNoIndex(feed.value!.resource_id, feed.value!.resource_type)
  )
  let meta = [
    {
      name: 'description',
      content: parseDescription(htmlToText(feed.value.content))
    },
    {
      name: 'keywords',
      content: (keywords.value?.map((k) => k.name) || [])
        .concat(
          `${feed.value.user.name}的动态`,
          `${feed.value.user.name}发表的文章`,
          `${feed.value.user.name}发表的视频`,
          `${feed.value.user.name}作品集`,
          `${feed.value.user.name}插画作品`
        )
        .join(',')
    }
  ]
  if (noIndex.value == 1) {
    meta.push({
      name: 'robots',
      content: 'noindex'
    })
  }
  useHead({
    title: parseTitle(
      htmlToText(feed.value.content).slice(0, 20) +
        '... - ' +
        feed.value.user.name +
        '的动态'
    ),
    link: [
      {
        rel: 'canonical',
        href: `https://www.mfuns.net/feed/${id}`
      },
      {
        rel: 'alternate',
        media: 'only screen and (max-width: 640px)',
        href: `https://m.mfuns.net/feed/${id}`
      }
    ],
    meta: meta
  })
}

const screen = useScreen()

const $toast = useToast()
const $confirm = useConfirm()
const { data: userInfo } = useAuth()
const isSelf = computed(() => feed.value?.user.id === userInfo.value?.user.id)

const { data: hotList } = useAsyncData(() => fetchHotPosts(5), {
  default: () => [] as Post.Info[]
})

function getAction(
  isAutoSync: boolean,
  resourceType: ResourceType,
  extraType: number
) {
  if (isAutoSync) {
    switch (resourceType) {
      case 0:
        return '发布了一篇文章'
      case 1:
        return '发布了一个视频'
      default:
        break
    }
  } else {
    switch (extraType) {
      case 0:
        return '发布了新动态'
      case 1:
      case 2:
      case 3:
        return '转发了'
      default:
        break
    }
  }
}

const action = computed(() => {
  return getAction(
    feed.value!.is_auto_sync,
    feed.value!.resource_type,
    feed.value!.extra_type
  )
})

const options: MenuOption[] = [
  {
    label: '删除动态',
    key: 'delete'
  }
]

async function handleSelect(key: string) {
  let res: any = {}
  switch (key) {
    case 'delete':
      $confirm('确认删除此动态？', '删除后无法恢复', {
        type: 'error',
        confirmButtonText: '删除',
        onConfirm: async () => {
          res = await requestDeleteFeed(feed.value!.id)
          if (res.code === 1) {
            $toast.success('已删除该动态')
            location.reload()
          } else {
            $toast.error(res.msg)
          }
        }
      })
      break
    default:
      break
  }
}
</script>

<template>
  <div v-if="feed" class="m-feed">
    <main class="m-feed__main">
      <m-image-preview>
        <article>
          <meta itemprop="headline" :content="feed.title" />
          <meta itemprop="keywords" :content="(keywords || []).join(',')" />
          <meta
            itemprop="datePublished"
            :content="dayjs(feed.created_at).format()"
          />
          <div itemprop="author" itemscope itemtype="http://schema.org/Person">
            <meta itemprop="name" :content="feed.user.name" />
            <meta
              itemprop="url"
              :content="`https://www.mfuns.net/member/${feed.user.id}`"
            />
          </div>
          <div
            itemprop="publisher"
            itemscope
            itemtype="http://schema.org/Organization"
          >
            <meta itemprop="name" content="喵御宅" />
            <div
              itemprop="logo"
              itemscope
              itemtype="https://schema.org/ImageObject"
            >
              <meta
                itemprop="url"
                content="https://www.mfuns.net/imgs/logo.svg"
              />
              <meta itemprop="width" content="180" />
              <meta itemprop="height" content="180" />
            </div>
          </div>
          <div class="m-feed-card__header">
            <m-user-info
              :id="feed.user.id"
              :avatar="feed.user.avatar"
              :badges="feed.user.badges"
              :name="feed.user.name"
              :auth="feed.user.user_auth"
              link
            >
              <header>
                <div class="m-feed__info">
                  <div class="m-feed__info-meta">
                    <time
                      :datetime="dayjs(feed.created_at * 1000).format()"
                      :title="
                        dayjs(feed.created_at * 1000)
                          .locale('zh-cn')
                          .format(
                            'ddd MMM DD YYYY HH:mm:ss [GMT]ZZ (中国标准时间)'
                          )
                      "
                      class="time"
                    >
                      {{
                        dayjs(feed.created_at * 1000).format('YYYY-MM-DD HH:mm')
                      }}
                    </time>
                    <span class="m-feed__info-meta-counts">
                      <m-icon icon="lucide-eye" />
                      <span>{{ feed.views }}</span>
                    </span>
                    <span class="m-feed__info-read-time">
                      <m-icon icon="lucide-timer" />
                      <span>{{ readTime }}分钟</span>
                    </span>
                  </div>
                </div>
              </header>
            </m-user-info>

            <n-dropdown
              v-if="isSelf"
              trigger="click"
              :options="options"
              @select="handleSelect"
            >
              <n-button text round class="more-button">
                <m-icon icon="lucide-more-vertical" />
              </n-button>
            </n-dropdown>
          </div>
          <div class="m-feed-card__body">
            <h3 v-if="feed.title" class="m-feed-card__h3">{{ feed.title }}</h3>
            <div
              class="m-feed-card__content"
              v-html="xss(setTargetBlank(feed.content))"
            ></div>
          </div>
          <div v-if="feed.extra.images?.length" class="m-feed-card__gallery">
            <m-image-preview>
              <m-gallery
                v-if="feed.extra.images.length > 1"
                :list="feed.extra.images"
              />
              <m-img
                v-else
                :src="feed.extra.images[0]"
                width="100%"
                class="preview"
              />
            </m-image-preview>
          </div>
          <div
            v-if="[1, 2, 3].includes(feed.extra_type)"
            class="m-feed-card__forward"
          >
            <feed-forward-feed
              v-if="feed.extra_type === 1"
              :id="feed.extra.resource.id"
              :title="feed.extra.resource.title"
              :content="feed.extra.resource.content"
              :images="feed.extra.resource.extra.images"
              :cover="feed.extra.resource.cover"
              :tags="feed.extra.resource.tag"
              :likes="feed.extra.resource.like_status?.like?.count"
              :comments="feed.extra.resource.floor_count"
              :views="feed.extra.resource.views"
              :user-id="feed.extra.user.id"
              :user-avatar="feed.extra.user.avatar"
              :badges="feed.extra.user.badges"
              :username="feed.extra.user.name"
              :user-auth="feed.extra.user.user_auth"
              :created-at="feed.extra.resource.created_at"
            />
            <feed-forward-article
              v-else-if="feed.extra_type === 2"
              :id="feed.extra.resource.id"
              :title="feed.extra.resource.title"
              :summary="feed.extra.resource.summary"
              :cover="feed.extra.resource.cover"
              :tags="feed.extra.resource.tag"
              :likes="feed.extra.resource.like_count"
              :comments="feed.extra.resource.comment_count"
              :views="feed.extra.resource.view_count"
              :user-id="feed.extra.user.id"
              :user-avatar="feed.extra.user.avatar"
              :badges="feed.extra.user.badges"
              :username="feed.extra.user.name"
              :user-auth="feed.extra.user.user_auth"
              :created-at="feed.extra.resource.created_at"
            />
            <feed-forward-video
              v-else-if="feed.extra_type === 3"
              :id="feed.extra.resource.id"
              :title="feed.extra.resource.title"
              :summary="feed.extra.resource.summary"
              :duration="feed.extra.resource.duration"
              :cover="feed.extra.resource.cover"
              :tags="feed.extra.resource.tag"
              :likes="feed.extra.resource.like_count"
              :comments="feed.extra.resource.comment_count"
              :views="feed.extra.resource.view_count"
              :user-id="feed.extra.user.id"
              :user-avatar="feed.extra.user.avatar"
              :badges="feed.extra.user.badges"
              :username="feed.extra.user.name"
              :user-auth="feed.extra.user.user_auth"
              :created-at="feed.extra.resource.created_at"
            />
          </div>
          <div v-if="feed.tags.length" class="m-feed-card__tags">
            <m-tag v-for="tag in feed.tags" :key="tag" link :label="tag" />
          </div>
        </article>
      </m-image-preview>
      <div id="comment" class="m-feed__comment-list">
        <m-comment-list ref="mCommentListRef" :area-id="feed.comment_area_id" />
      </div>
    </main>
    <aside class="m-feed__side">
      <m-container class="m-feed__author">
        <m-author :user="feed.user" size="large" follow :row="2" />
      </m-container>
      <m-container title="热门内容" padding>
        <m-space vertical gap="12px">
          <m-post-card-leaderboard
            v-for="(p, i) in hotList"
            :key="p.id"
            :post="p"
            :rank="i + 1"
          />
        </m-space>
      </m-container>
    </aside>
    <client-only>
      <feed-side-action v-if="!screen.xs" :feed="feed" />
    </client-only>
  </div>
</template>

<style lang="scss" scoped>
@media screen and (max-width: 600px) {
  .m-feed {
    background-color: #fff;
    padding: 12px 24px;
    .open-app {
      width: 100%;
      text-align: center;
      line-height: 36px;
      height: 36px;
      background-color: var(--color-gray-100);
    }
    &__main {
      padding: 0 !important;
    }
    &__side {
      display: none;
    }
  }
}
.m-feed {
  display: flex;
  &__main {
    flex: 1;
    width: 0;
    article {
      padding: 32px;
      background-color: #fff;
      border-radius: 4px;
    }
    @media screen and (max-width: 800px) {
      article {
        padding: 0px;
      }
    }
  }

  &-card {
    border-bottom: 1px solid var(--color-gray-200);
    margin-bottom: 24px;
    content-visibility: auto;
    contain-intrinsic-size: 400px;
    &__header {
      display: flex;
      .more-button {
        margin-left: auto;
        font-size: 24px;
      }
    }
    &__body {
      color: var(--text-secondary);
      font-size: 14px;
    }
    &__h3 {
      font-size: 16px;
      line-height: 20px;
      font-weight: 600;
      color: #333;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin: 0;
      padding: 0;
    }
    &__gallery {
      margin-top: 5px;
      width: 100%;
    }
    &__content {
      margin-top: 5px;
    }
    &__forward {
      margin-top: 5px;
      padding: 12px;
      background-color: var(--color-stone-50);
      border-radius: 8px;
    }
    &__tags {
      margin-top: 12px;
    }
  }
  &__title {
    font-size: 32px;
    margin-bottom: 16px;
  }
  &__info {
    &-meta {
      font-size: 14px;
      color: var(--color-gray-400);
      display: flex;
      align-items: center;
      > * {
        margin-right: 16px;
        display: flex;
        align-items: center;
        .m-icon {
          margin-right: 4px;
        }
      }
    }
  }
  &__side {
    width: 300px;
    margin-left: 12px;
    > * {
      margin-bottom: 12px;
    }
  }
  &__author {
    padding: 20px;
    background-color: #fff;
  }
}
</style>

<style lang="scss">
article {
  font-size: 16px;
  .preview {
    display: block;
    max-width: 100%;
    margin: 0 auto;
  }
  h1 {
    font-size: 22px;
  }
  h2 {
    font-size: 20px;
  }
  h3 {
    font-size: 18px;
  }
  h4 {
    font-size: 16px;
  }
  h5 {
    font-size: 14px;
  }
  p {
    margin: 0 0 16px;
    text-align: justify;
    color: var(--color-gray-700);
    line-height: 1.5;
  }
}
</style>
